import { useState, useRef, useEffect, useLayoutEffect } from "react";
import Loading from "./Loading";
import MatchBookModal from "./MatchBookModal";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const VISIBLE_ROWS = 10; // itni rows dikhengi, baaki scroll se
const PAGE_LIMIT = 50;   // ek page me itni bets aati hain (API limit)

const BetsTable = ({
  eventBets,
  isLoading,
  isFetching,
  currentPage = 1,
  onPageChange,
  beventId,
  activeTab = null,   // null = koi tab open nahi (default). Parent (MatchDetailPage) control karta hai.
  onTabChange,
  onRefresh,
  defaultCounts,      // tab band ho tab ke counts (MatchDetailPage ki halki call se)
}) => {
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [bookClient, setBookClient] = useState(null); // {id, label} — Match Book modal
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);
  const tableRef = useRef(null);

  const matchBets   = eventBets?.data?.bookmaker || [];
  const sessionBets = eventBets?.data?.fancy     || [];
  const tossBets    = eventBets?.data?.toss      || [];
  const counts      = (activeTab && eventBets?.counts) || defaultCounts || eventBets?.counts || { bookmaker: 0, fancy: 0, toss: 0 };
  const pagination  = eventBets?.pagination || {};

  const getActivePagination = () => {
    if (activeTab === "session") return pagination.fancy || {};
    if (activeTab === "toss")    return pagination.toss  || {};
    return pagination.bookmaker || {};
  };
  const activePagination = getActivePagination();
  const getActiveTotalPages = () => activePagination.totalPages || 0;
  const activeTotal = activePagination.total || 0;
  const limit = pagination.limit || PAGE_LIMIT;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowSearchDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getActiveBets = () => {
    if (activeTab === "session") return sessionBets;
    if (activeTab === "toss")    return tossBets;
    return matchBets;
  };

  const activeBets = getActiveBets();

  // Table ki height = header + pehli 10 rows, taaki 10 bets dikhein aur baaki scroll ho
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (!box) return;
    const table = tableRef.current;
    const rows = table?.tBodies[0]?.rows || [];
    if (!table || rows.length <= VISIBLE_ROWS) { box.style.maxHeight = ""; return; }
    let h = table.tHead?.offsetHeight || 0;
    for (let i = 0; i < VISIBLE_ROWS; i++) h += rows[i].offsetHeight;
    // horizontal scrollbar ki jagah bhi jodo
    h += box.offsetHeight - box.clientHeight;
    box.style.maxHeight = `${h}px`;
  }, [activeBets, activeTab]);

  // Page ya tab badle to list upar se shuru ho
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [currentPage, activeTab]);

  const goToPage = (p) => {
    const total = getActiveTotalPages();
    if (p < 1 || (total && p > total) || p === currentPage) return;
    onPageChange?.(p);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2,'0');
    const sec = String(d.getSeconds()).padStart(2,'0');
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${String(h).padStart(2,'0')}:${min}:${sec} ${ampm}`;
  };

  const tabs = [
    { key: "match",   label: "Match",   count: counts.bookmaker },
    { key: "session", label: "Session", count: counts.fancy },
    { key: "toss",    label: "Toss",    count: counts.toss || 0 },
  ];

  return (
    <div className="bets-table bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-4 py-2.5 flex items-center gap-2.5">
        <div className="w-1 h-5 bg-blue-400 rounded-full" />
        <span className="text-white font-semibold text-sm tracking-wide">Bets</span>
      </div>

      {/* Tabs — default koi active nahi. Tab click = khulega (API call), wahi tab dubara click = band. */}
      <div className="flex bg-white px-3 gap-1 items-center">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              if (activeTab === tab.key) {
                onTabChange?.(null);          // dubara click -> band
              } else {
                onTabChange?.(tab.key);       // open -> yahi par API call hoti hai
                onPageChange?.(1);
              }
            }}
            className={`px-3 py-2 my-1 text-xs font-semibold rounded-md transition-all ${
              activeTab === tab.key
                ? 'bg-teal-500 text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}
          >
            {tab.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === tab.key ? 'bg-white text-teal-600' : 'bg-teal-500 text-white'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}

        {/* Refresh — sirf tab open hone par; isi button pe API dubara call hoti hai */}
        {activeTab && (
          <button
            onClick={() => onRefresh?.()}
            disabled={isFetching}
            title="Refresh bets"
            className="ml-auto my-1 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold rounded-md bg-[#0c1524] text-white hover:bg-[#162032] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`}>
              <path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      {/* Table */}
      <div
        ref={scrollRef}
        className="overflow-auto"
      >
        {!activeTab ? (
          <div className="flex items-center justify-center gap-2 py-4 text-slate-400">
            <svg className="w-4 h-4 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <p className="text-xs font-medium">Upar se tab chuno — bets dekhne ke liye</p>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loading />
          </div>
        ) : activeBets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <svg className="w-10 h-10 mb-2 text-slate-300" viewBox="0 0 64 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="32" cy="33" rx="32" ry="7" fill="#e2e8f0"/>
              <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#cbd5e1"/>
              <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#cbd5e1"/>
            </svg>
            <p className="text-xs font-medium">No bets found</p>
          </div>
        ) : (
          <table ref={tableRef} className="w-full text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#0F2327] text-white">
                {activeTab === "session" && <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Volume</th>}
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Amount</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Rate</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Type</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Team</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    Client
                    <div ref={dropdownRef} className="inline-block relative">
                      <button onClick={() => setShowSearchDropdown(!showSearchDropdown)} className="text-white/70 hover:text-white">
                        <SearchIcon />
                      </button>
                      {showSearchDropdown && (
                        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-50 min-w-[200px] animate-slideDown">
                          <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search client..."
                            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 mb-2"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button onClick={() => { setSearchValue(''); setShowSearchDropdown(false); }}
                              className="flex-1 px-2 py-1.5 bg-slate-100 text-slate-600 text-xs rounded-lg hover:bg-slate-200">
                              Cancel
                            </button>
                            <button onClick={() => setShowSearchDropdown(false)}
                              className="flex-1 px-2 py-1.5 bg-[#0c1524] text-white text-xs rounded-lg hover:bg-[#162032]">
                              Search
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Time</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Loss</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Profit</th>
              </tr>
            </thead>
            <tbody>
              {activeBets.map((bet, index) => {
                const isLagaiOrYes = ["lagai", "yes", "back"].includes(bet.type?.toLowerCase());
                return (
                  <tr key={bet.betId || index} className={`border-b border-slate-100 ${isLagaiOrYes ? 'bet-row-back' : 'bet-row-lay'}`}>
                    {activeTab === "session" && <td className="px-2 py-2 text-sm font-semibold whitespace-nowrap text-slate-600">{bet.size || '-'}</td>}
                    <td className="px-2 py-2 text-sm font-semibold text-slate-800">{bet.amount || '-'}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700">{bet.rate || '-'}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        isLagaiOrYes ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                      }`}>
                        {bet.type || '-'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700 whitespace-nowrap">{bet.team || bet.selectionName || '-'}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700 whitespace-nowrap">
                      {bet.client ? (
                        activeTab === "match" ? (
                          <button
                            type="button"
                            onClick={() => setBookClient({ id: bet.client, label: bet.client })}
                            className="text-left text-teal-600 hover:text-teal-800 hover:underline font-semibold"
                            title="Match Book"
                          >
                            {bet.client}
                          </button>
                        ) : (
                          <span>{bet.client}</span>
                        )
                      ) : '-'}
                      {bet.fullName && <div className="text-xs text-slate-500 font-medium">{bet.fullName}</div>}
                    </td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-500 whitespace-nowrap">{formatTime(bet.date || bet.createdAt)}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-red-500">{(bet.loss || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-emerald-600">{(bet.profit || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination: 50 bets per page (match / session / toss alag-alag) */}
      {activeTab && getActiveTotalPages() > 1 && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-slate-500">
            {Math.min((currentPage - 1) * limit + 1, activeTotal)}–{Math.min(currentPage * limit, activeTotal)} of {activeTotal}
            <span className="ml-2 text-slate-400">(Page {currentPage} / {getActiveTotalPages()})</span>
          </span>
          <div className="flex gap-1.5">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage <= 1}
              className="px-2.5 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              «
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= getActiveTotalPages()}
              className="px-3 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              Next
            </button>
            <button
              onClick={() => goToPage(getActiveTotalPages())}
              disabled={currentPage >= getActiveTotalPages()}
              className="px-2.5 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              »
            </button>
          </div>
        </div>
      )}

      {bookClient && (
        <MatchBookModal
          beventId={beventId}
          clientId={bookClient.id}
          clientLabel={bookClient.label}
          onClose={() => setBookClient(null)}
        />
      )}
    </div>
  );
};

export default BetsTable;
